import {
  Chip,
  Grid,
  Button,
  Switch,
  Tooltip,
  ListItem,
  TextField,
  FormControlLabel,
  FormControl,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { makeStyles } from "@mui/styles";

import { useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import { Add, Delete, Edit } from "@mui/icons-material";

import "./MedicationInformation.css";

import Loading from "components/common/loading/Loading";
import SubmitButton from "components/Button/SubmitButton";

import {
  addPatientMedication,
  getPatientMedications,
} from "app/services/transmission/patient-medication";

import { showToast } from "utils/toast";
import { getFormattedDate } from "utils/date";
import dayjs from "dayjs";
import { DATE_FORMAT } from "static/date";
import { FieldArray, Formik } from "formik";
import * as Yup from "yup";

const MedicationInformation = () => {
  const medicationInformation = {
    medicine_information: "",
    medicine_start_date: "",
    medicine_end_date: "",
    active: true,
  };

  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingList, setIsFetchingList] = useState(false);
  const [medicationList, setMedicationList] = useState();
  const [initialMedicationList, setInitialMedicationList] = useState([]);
  const [, setMedicationFormData] = useState([medicationInformation]);

  const [hasIncompleteMedicalInformation, setHasIncompleteMedicalInformation] =
    useState(false);

  const { patientId } = useSelector((state) => state.transmission);

  const useStyles = makeStyles({
    switchRoot: {
      marginLeft: "-10px",
    },
  });

  const classes = useStyles();

  const validationSchema = Yup.object().shape({
    medications: Yup.array()
      .of(
        Yup.object().shape({
          medicine_information: Yup.string().required("Medication is required"),
          medicine_start_date: Yup.string() // Keep as string to process raw input
            .required("Start date is required")
            .test(
              "is-valid-date",
              "Start date must be a valid date",
              (value) => {
                if (!value) return false; // Required field validation
                const parsedDate = new Date(value);
                return !isNaN(parsedDate.getTime()); // Check if it's a valid date
              }
            ),
          medicine_end_date: Yup.string() // Keep as string to process raw input
            .nullable()
            .test("is-valid-date", "End date must be a valid date", (value) => {
              if (!value) {
                return true; // Skip validation if value is null or empty
              }
              const parsedDate = new Date(value);
              return !isNaN(parsedDate.getTime()); // Check if it's a valid date
            }),
          active: Yup.boolean(),
        })
      )
      .required("At least one medication is required"),
  });

  useEffect(() => {
    fetchMedicationList();
  }, []);

  const fetchMedicationList = async () => {
    try {
      setIsFetchingList(true);
      const result = await getPatientMedications(patientId);
      const medicationData = result?.data?.medications || [];
      if (medicationData.length > 0) {
        mapMedicationData(medicationData);
      }
    } catch (error) {
      showToast({ message: error.message, type: "error" });
    } finally {
      setIsFetchingList(false);
    }
  };

  const mapMedicationData = (medicationData) => {
    const medicationList = medicationData.map((medData) => ({
      medicine_information: medData.medicine_information,
      medicine_start_date: medData.medicine_start_date,
      medicine_end_date: medData.medicine_end_date,
      active: medData.active,
    }));
    setMedicationList(medicationList);
    setInitialMedicationList(medicationList);
  };

  const handleSubmit = async (values) => {
    try {
      setIsLoading(true);

      const filteredMedicationData = values?.medications
        ?.filter(
          (data) =>
            data.medicine_information !== "" && data.medicine_start_date !== ""
        )
        .map(
          ({
            medicine_start_date_invalid,
            medicine_end_date_invalid,
            ...rest
          }) => rest
        );

      const payload = {
        patientId,
        medicationData: filteredMedicationData,
      };

      const response = await addPatientMedication(payload);

      if (!response.error) {
        setIsEditMode(false);
        showToast({ message: response.message });
        setHasIncompleteMedicalInformation(false);
        await fetchMedicationList();
      }
    } catch (error) {
      showToast({ message: error.message, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = () => {
    if (medicationList && medicationList.length > 0) {
      const copy = [...medicationList];
      setMedicationFormData(copy);
    }
    setIsEditMode(true);
  };

  const renderMedicationInformationPreview = () => {
    if (isFetchingList) {
      return <Loading height="50px" />;
    }

    if (!medicationList) {
      return (
        <div className="d-flex justify-center">
          No medication information found.
        </div>
      );
    }

    return medicationList.map((data, index) => {
      const {
        medicine_information,
        medicine_start_date,
        medicine_end_date,
        active,
      } = data;

      const numbering = index + 1;

      return (
        <Grid
          key="medication_data_index"
          container
          columnSpacing={3}
          className="medication-information__list"
          margin={0}
        >
          <ListItem className="MuiTypography-body1">
            <span className="medication-information__numbering mr-2">
              {numbering}.
            </span>

            <Tooltip title={medicine_information}>
              <div className="medication-information__preview-text">
                <span>{medicine_information}</span>
              </div>
            </Tooltip>

            <span>
              , {medicine_start_date}
              {medicine_end_date && ` - ${medicine_end_date}`}
            </span>
            <Chip
              label={active ? "Active" : "Inactive"}
              className={`ml-2 medication-information__chip_${
                active ? "active" : "inactive"
              }`}
            />
          </ListItem>
        </Grid>
      );
    });
  };

  const renderMedicationInformationForm = () => {
    return (
      <Formik
        initialValues={{ medications: initialMedicationList }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          setFieldValue,
          handleChange,
          errors,
          touched,
          handleSubmit,
          resetForm,
        }) => {
          return (
            <>
              <FieldArray name="medications">
                {({ remove, push }) => (
                  <>
                    {values.medications.map((medication, index) => (
                      <Grid
                        container
                        spacing={1}
                        alignItems="flex-start"
                        key={index}
                      >
                        <Grid container item columnSpacing={1}>
                          <Grid item style={{ width: "30%" }}>
                            <TextField
                              required
                              fullWidth
                              name={`medications[${index}].medicine_information`}
                              variant="outlined"
                              margin="none"
                              className="trans-table__filterDatePicker"
                              label="Medication"
                              value={medication.medicine_information}
                              onChange={handleChange}
                              error={
                                touched?.medications?.[index]
                                  ?.medicine_information &&
                                !!errors?.medications?.[index]
                                  ?.medicine_information
                              }
                              helperText={
                                touched?.medications?.[index]
                                  ?.medicine_information &&
                                errors?.medications?.[index]
                                  ?.medicine_information
                              }
                            />
                          </Grid>
                          <Grid item style={{ width: "26%" }}>
                            <FormControl fullWidth margin="none">
                              <DatePicker
                                required
                                autoOk
                                fullWidth
                                margin="none"
                                inputVariant="outlined"
                                label="Start Date"
                                className="trans-table__filterDatePicker"
                                name={`medications[${index}].medicine_start_date`}
                                format={DATE_FORMAT.DEFAULT}
                                maxDate={
                                  dayjs(medication.medicine_end_date) ||
                                  dayjs("2100-12-31")
                                }
                                KeyboardButtonProps={{
                                  "aria-label": "pick date",
                                }}
                                value={
                                  medication.medicine_start_date
                                    ? dayjs(medication.medicine_start_date)
                                    : null
                                }
                                onChange={(date) => {
                                  const formattedDate = getFormattedDate(date);

                                  setFieldValue(
                                    `medications[${index}].medicine_start_date`,
                                    formattedDate
                                  );
                                }}
                                slotProps={{
                                  textField: {
                                    fullWidth: true,
                                    required: true,
                                    error:
                                      touched?.medications?.[index]
                                        ?.medicine_start_date &&
                                      !!errors?.medications?.[index]
                                        ?.medicine_start_date,
                                    helperText:
                                      touched?.medications?.[index]
                                        ?.medicine_start_date &&
                                      errors?.medications?.[index]
                                        ?.medicine_start_date,
                                  },
                                }}
                              />
                            </FormControl>
                          </Grid>
                          <Grid item style={{ width: "26%" }}>
                            <FormControl fullWidth margin="none">
                              <DatePicker
                                autoOk
                                fullWidth
                                margin="none"
                                inputVariant="outlined"
                                label="End Date"
                                className="trans-table__filterDatePicker"
                                name={`medications[${index}].medicine_end_date`}
                                format={DATE_FORMAT.DEFAULT}
                                minDate={
                                  dayjs(medication.medicine_start_date) ||
                                  dayjs("1900-01-01")
                                }
                                KeyboardButtonProps={{
                                  "aria-label": "pick date",
                                }}
                                value={
                                  medication.medicine_end_date
                                    ? dayjs(medication.medicine_end_date)
                                    : null
                                }
                                onChange={(date) => {
                                  setFieldValue(
                                    `medications[${index}].medicine_end_date`,
                                    getFormattedDate(date)
                                  );
                                }}
                                slotProps={{
                                  textField: {
                                    fullWidth: true,
                                    error:
                                      touched?.medications?.[index]
                                        ?.medicine_end_date &&
                                      !!errors?.medications?.[index]
                                        ?.medicine_end_date,
                                    helperText:
                                      touched?.medications?.[index]
                                        ?.medicine_end_date &&
                                      errors?.medications?.[index]
                                        ?.medicine_end_date,
                                  },
                                }}
                              />
                            </FormControl>
                          </Grid>
                          <Grid
                            container
                            alignItems="flex-start"
                            style={{
                              width: "18%",
                              paddingLeft: "24px",
                              marginTop: "8px",
                            }}
                          >
                            <Grid
                              item
                              display="flex"
                              alignItems="center"
                              gap={2}
                            >
                              <FormControlLabel
                                control={
                                  <Switch
                                    name={`medications[${index}].active`}
                                    color="secondary"
                                    checked={medication.active}
                                    onChange={handleChange}
                                    className={classes.switchRoot}
                                  />
                                }
                                label="Active"
                                labelPlacement="start"
                                style={{ gap: "6px", marginLeft: 0 }}
                              />
                              {values.medications.length > 1 && (
                                <Delete
                                  className="mr-2 medication-information__delete"
                                  onClick={() => remove(index)}
                                />
                              )}
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    ))}
                    <span
                      className="medication-information__add-medication"
                      onClick={() => push(medicationInformation)}
                    >
                      <Add className="medication-information__add-icon" />
                      <span className="medication-information__add-text">
                        Add Medication
                      </span>
                    </span>
                  </>
                )}
              </FieldArray>
              <div
                style={{ textAlign: "right", gap: "10px" }}
                className="d-flex flex-end"
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    fetchMedicationList();
                    setIsEditMode(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <SubmitButton
                  variant="contained"
                  color="secondary"
                  handleClick={handleSubmit}
                  isLoading={isLoading}
                  disabled={hasIncompleteMedicalInformation}
                />
              </div>
            </>
          );
        }}
      </Formik>
    );
  };

  return (
    <div className="patient-modal__section">
      <div className="patient-form__row patient-form__info">
        <div className="mb-3">
          <Grid container>
            <Grid item xs={11} style={{ textAlign: "left" }}>
              <div className="patient-form__info--header">
                Medication Information
              </div>
            </Grid>
            {!isEditMode && !isFetchingList && (
              <Grid item xs={1} style={{ textAlign: "right" }}>
                <Button
                  variant="text"
                  color="secondary"
                  className="profile-edit-button"
                  onClick={handleEditClick}
                >
                  <Edit className="patient-edit-icon billing-cycle-edit-icon" />
                  Edit
                </Button>
              </Grid>
            )}
          </Grid>
          {isEditMode
            ? renderMedicationInformationForm()
            : renderMedicationInformationPreview()}
        </div>
      </div>
    </div>
  );
};

export default MedicationInformation;
